import type {
  FinishResponse,
  InputMode,
  Move,
  NudgeAnswer,
  PersonStatus,
  RatingResponse,
  SolveDto,
  SolveResponse,
  Thumb,
  UserSettings,
} from '@zinc/shared';
import { useQuery } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import { Splash } from '../components/AppShell';
import { gridFrame } from '../components/solve/constraints';
import { AddToCollectionDialog } from '../components/library/CollectionTicks';
import { FinishDialog } from '../components/solve/FinishDialog';
import { Grid } from '../components/solve/Grid';
import { Menu, menuIcons, type MenuAction } from '../components/solve/Menu';
import { NudgeCard } from '../components/solve/NudgeCard';
import { ReplayBar } from '../components/solve/ReplayBar';
import { Overview } from '../components/solve/Overview';
import { Pad, type Tool } from '../components/solve/Pad';
import { RulesStrip, rulesSummary } from '../components/solve/RulesStrip';
import { SettingsSheet } from '../components/solve/SettingsSheet';
import { SolveHeader } from '../components/solve/SolveHeader';
import { ZoomView, type ZoomHandle } from '../components/solve/ZoomView';
import { api } from '../lib/api';
import { core, coreAvailable } from '../lib/core/client';
import {
  applyMove,
  canRedo,
  canUndo,
  current,
  givenShows,
  houseConflicts,
  isFull,
  matchingCandidates,
  replay,
  startHistory,
  typed,
  values,
  wrongCells,
  type GridState,
  type History,
} from '../lib/game';
import { NO_WRONG_MARKS, wrongMarks } from '../lib/checking';
import { buildGeometry, cellAt, type Geometry } from '../lib/geometry';
import { useMe } from '../lib/queries';
import { MoveSync, storedMoves } from '../lib/sync';

const SOURCE_LABELS: Record<string, string> = {
  import: 'Imported',
  ctc: 'Cracking the Cryptic',
  bremster: 'BremSter',
  lmd: 'Logic Masters',
  artisanal: 'Artisanal Sudoku',
  gmpuzzles: 'GMPuzzles',
  // The origin already says "generated".
  generator: '',
  builder: 'Built here',
};

export function SolvePage() {
  const { id = '' } = useParams();
  const me = useMe();
  const query = useQuery({
    queryKey: ['solve', id],
    queryFn: () => api<SolveResponse>(`/api/puzzles/${id}/solve`, { method: 'POST' }),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  if (me.isError || query.isError) {
    return (
      <Splash
        message={query.error?.message ?? "ZincSudoku can't reach the server. Try again shortly."}
      />
    );
  }
  if (!me.data || !query.data) return <Splash />;
  return (
    <SolveScreen
      key={query.data.solve.id}
      data={query.data}
      settings={me.data.settings}
      otherName={me.data.other?.displayName ?? null}
    />
  );
}

type Marked = ReadonlySet<number>;

/** The cells of the latest moves, most recent first (where a nudge looks first). */
function recentOf(moves: Move[]): number[] {
  const out: number[] = [];
  for (let k = moves.length - 1; k >= 0 && out.length < 12; k--) {
    for (const c of moves[k]!.cells) if (!out.includes(c)) out.push(c);
  }
  return out.slice(0, 12);
}

/** Every state the move log passes through, for replay. */
function replayStates(g: Geometry, moves: Move[]): GridState[] {
  let h = startHistory(g);
  const out = [current(h)];
  for (const m of moves) {
    h = applyMove(g, h, m);
    out.push(current(h));
  }
  return out;
}

/** Entered digits that repeat in a house (givens are never marked). */
function conflictsOf(geo: Geometry, s: GridState): number[] {
  const shows = givenShows(geo, s);
  return houseConflicts(geo, s).filter((i) => !shows(i));
}

function useTimer(initialMs: number, running: boolean) {
  const elapsed = useRef(initialMs);
  const [shown, setShown] = useState(initialMs);
  useEffect(() => {
    if (!running) return;
    let last = performance.now();
    const tick = () => {
      const now = performance.now();
      elapsed.current += now - last;
      last = now;
      setShown(elapsed.current);
    };
    const id = setInterval(tick, 250);
    return () => {
      clearInterval(id);
      tick();
    };
  }, [running]);
  const reset = (ms: number) => {
    elapsed.current = ms;
    setShown(ms);
  };
  return { elapsed, shown, reset };
}

function useWakeLock(active: boolean) {
  useEffect(() => {
    const nav = navigator as Navigator & {
      wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    if (!active || !nav.wakeLock) return;
    let lock: { release: () => Promise<void> } | null = null;
    let cancelled = false;
    const acquire = () => {
      if (document.visibilityState !== 'visible') return;
      nav.wakeLock
        .request('screen')
        .then((l) => {
          if (cancelled) void l.release();
          else lock = l;
        })
        .catch(() => {});
    };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', acquire);
      void lock?.release().catch(() => {});
    };
  }, [active]);
}

function SolveScreen({
  data,
  settings,
  otherName,
}: {
  data: SolveResponse;
  settings: UserSettings;
  otherName: string | null;
}) {
  const navigate = useNavigate();
  const { puzzle } = data;
  const doc = puzzle.document;
  const g: Geometry = useMemo(() => buildGeometry(doc), [doc]);
  const frame = useMemo(() => gridFrame(doc), [doc]);
  // 16×16 and samurai grids zoom and pan; samurai also gets the overview map.
  const zoomable = Math.max(g.rows, g.cols) > 9;
  const zoom = useRef<ZoomHandle>(null);

  const [solve, setSolve] = useState<SolveDto>(data.solve);
  // Moves an earlier visit couldn't send are replayed on top of the server's log.
  const [stored] = useState(() => storedMoves(data.solve.id));
  const [history, setHistoryState] = useState<History>(() =>
    replay(g, [...data.solve.moves, ...stored]),
  );
  const historyRef = useRef(history);
  const setHistory = useCallback((h: History) => {
    historyRef.current = h;
    setHistoryState(h);
  }, []);
  const state = current(history);

  const [inputMode, setInputMode] = useState<InputMode>(data.solve.mode);
  const [chosenTool, setTool] = useState<Tool>('digit');
  // The select tool: while on, a tap adds or removes a cell (as Ctrl/Shift-click does).
  const [selectOn, setSelectOn] = useState(false);
  const selecting = selectOn && settings.showSelectTool;
  // The colour and pen tools are optional (Settings): switched off, they can't stay chosen.
  const showColour = settings.showColourTool;
  const showPen = settings.showPenTool;
  const { showCornerMarks, showCentreMarks, showPositionMarks } = settings;
  const markButtons = useMemo(
    () => ({ corner: showCornerMarks, centre: showCentreMarks, position: showPositionMarks }),
    [showCornerMarks, showCentreMarks, showPositionMarks],
  );
  // A mark mode switched off while it's on goes back to digits (its keys can still pick it,
  // so this happens only when the setting changes).
  const [marksBefore, setMarksBefore] = useState(markButtons);
  if (marksBefore !== markButtons) {
    setMarksBefore(markButtons);
    if (
      (chosenTool === 'corner' && !markButtons.corner) ||
      (chosenTool === 'centre' && !markButtons.centre) ||
      (chosenTool === 'position' && !markButtons.position)
    )
      setTool('digit');
  }
  const tool: Tool =
    (chosenTool === 'colour' && !showColour) || (chosenTool === 'pen' && !showPen)
      ? 'digit'
      : chosenTool;
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const selectionRef = useRef(selection);
  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);
  const [paused, setPaused] = useState(false);
  const [finished, setFinished] = useState(data.solve.finishedAt !== null);
  const [finishInfo, setFinishInfo] = useState<{
    timeMs: number;
    nudges: number;
    other: PersonStatus | null;
  } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addingToCollection, setAddingToCollection] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [checked, setChecked] = useState<Marked | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [variantConflicts, setVariantConflicts] = useState<number[]>([]);
  const [online, setOnline] = useState(true);
  const mistakes = useRef(data.solve.mistakes);
  const [mistakesShown, setMistakesShown] = useState(data.solve.mistakes);
  const nudges = useRef(data.solve.nudges);
  // Every move of this attempt, in order (for replay), and the cells touched last.
  const allMoves = useRef<Move[]>([...data.solve.moves, ...stored]);
  const recentCells = useRef<number[]>(recentOf([...data.solve.moves, ...stored]));
  const [nudge, setNudge] = useState<{ answer: NudgeAnswer; level: number } | null>(null);
  const nudgeBusy = useRef(false);
  const [rating, setRating] = useState<Thumb | null>(puzzle.myRating);
  const [replaying, setReplaying] = useState<{
    states: GridState[];
    at: number;
    playing: boolean;
  } | null>(null);
  const inputModeRef = useRef(inputMode);
  useEffect(() => {
    inputModeRef.current = inputMode;
  }, [inputMode]);

  const running = !paused && !finished;
  const timer = useTimer(data.solve.finalTimeMs ?? data.solve.elapsedMs, running);
  useWakeLock(running && settings.keepScreenOn);

  // Move sync: one queue per attempt.
  const syncRef = useRef<MoveSync | null>(null);
  const makeSync = useCallback(
    (s: SolveDto) => {
      syncRef.current?.dispose();
      const sync = new MoveSync(s.id, puzzle.id, s.moves.length, () => ({
        state: current(historyRef.current),
        elapsedMs: Math.round(timer.elapsed.current),
        mistakes: mistakes.current,
        nudges: nudges.current,
        mode: inputModeRef.current,
      }));
      sync.onStatus = (isOnline) => setOnline(isOnline);
      sync.onRebased = (moves) => {
        allMoves.current = moves;
        setHistory(replay(g, moves));
      };
      syncRef.current = sync;
      return sync;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [g, puzzle.id, setHistory],
  );
  useEffect(() => {
    const sync = makeSync(data.solve);
    if (stored.length > 0) sync.restore(stored);
    const leave = () => void sync.flush();
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('pagehide', leave);
      void sync.flush().finally(() => sync.dispose());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause when the app goes to the background or the screen locks.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        setPaused(true);
        void syncRef.current?.flush();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Save the clock now and then even without moves.
  useEffect(() => {
    if (!running) {
      syncRef.current?.touch();
      return;
    }
    const id = setInterval(() => syncRef.current?.touch(), 15_000);
    return () => clearInterval(id);
  }, [running]);

  // Variant rules are checked by the core in a worker; classic repeats are checked here. A
  // puzzle that needs a plugin plays without rule checking: its rules aren't all read, and
  // some may be read wrongly.
  const needsPlugin = puzzle.status === 'needs-plugin';
  const vals = useMemo(() => values(g, state), [g, state]);
  useEffect(() => {
    if (needsPlugin || doc.constraints.length === 0 || !coreAvailable()) return;
    let stale = false;
    core
      .check(doc, vals)
      .then((list) => {
        if (stale) return;
        setVariantConflicts(
          list.flatMap((c) => c.cells.map(([r, col]) => cellAt(g, r, col))).filter((i) => i >= 0),
        );
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [doc, g, vals, needsPlugin]);

  // Every digit in a conflict, givens included: two 2s in a row both turn red.
  const conflictCells = useMemo(
    () => new Set([...houseConflicts(g, state), ...variantConflicts]),
    [g, state, variantConflicts],
  );
  // Digits to show in red at the mistake-checking level: Solution adds what the solution rules
  // out to the conflicts.
  const mistakeCells = useMemo((): Set<number> => {
    if (settings.mistakeChecking === 'off') return new Set();
    if (settings.mistakeChecking === 'solution' && g.solution)
      return new Set([...conflictCells, ...wrongCells(g, state)]);
    return conflictCells;
  }, [settings.mistakeChecking, g, state, conflictCells]);
  // "Check digits" and "Check pencil marks" (Settings): as you go, in both modes.
  const digitsLive = settings.mistakeChecking !== 'off' && settings.checkDigits;
  const shownWrongMarks = useMemo(
    () =>
      settings.checkMarks
        ? wrongMarks(g, state, settings.mistakeChecking, inputMode)
        : NO_WRONG_MARKS,
    [settings.checkMarks, settings.mistakeChecking, g, state, inputMode],
  );

  const wrongNow = useCallback(
    (s = current(historyRef.current)): Set<number> =>
      settings.mistakeChecking === 'solution' && g.solution
        ? new Set(wrongCells(g, s))
        : new Set(conflictsOf(g, s)),
    [g, settings.mistakeChecking],
  );

  // Digits in red: as you go when "Check digits" is on; otherwise SudokuPad mode's Check.
  const shownWrong: Marked = useMemo(() => {
    if (digitsLive) return mistakeCells;
    if (inputMode === 'simple') return new Set();
    return checked ?? new Set();
  }, [digitsLive, inputMode, mistakeCells, checked]);

  const showToast = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast((t) => (t === text ? null : t)), 2600);
  }, []);

  const finish = useCallback(async () => {
    const s = current(historyRef.current);
    const sync = syncRef.current;
    await sync?.flush();
    const res = await api<FinishResponse>(`/api/solves/${solve.id}/finish`, {
      method: 'POST',
      json: { values: values(g, s), elapsedMs: Math.round(timer.elapsed.current) },
    }).catch(() => null);
    if (!res) {
      showToast("Solved! It couldn't be saved yet; it will be when you're back online.");
      return;
    }
    if (res.correct) {
      setFinished(true);
      setSolve(res.solve);
      timer.reset(res.solve.finalTimeMs ?? timer.elapsed.current);
      setFinishInfo({
        timeMs: res.solve.finalTimeMs ?? timer.elapsed.current,
        nudges: nudges.current,
        other: res.other,
      });
    } else {
      showToast('Not quite: something in the grid is wrong.');
    }
  }, [g, solve.id, showToast, timer]);

  const doMove = useCallback(
    (partial: Omit<Move, 't'>) => {
      if (finished || paused) return;
      const move: Move = { ...partial, t: Math.round(timer.elapsed.current) };
      const before = historyRef.current;
      const next = applyMove(g, before, move);
      if (next === before) return;
      setHistory(next);
      syncRef.current?.push(move);
      allMoves.current.push(move);
      if (move.cells.length > 0) {
        recentCells.current = [
          ...move.cells,
          ...recentCells.current.filter((c) => !move.cells.includes(c)),
        ].slice(0, 12);
      }
      setNudge(null);
      setChecked(null);
      const s = current(next);
      if (move.kind === 'digit' && inputModeRef.current === 'simple' && digitsLive) {
        const wrongBefore = wrongNow(current(before));
        const fresh = [...wrongNow(s)].filter((i) => !wrongBefore.has(i) && move.cells.includes(i));
        if (fresh.length > 0) {
          mistakes.current += fresh.length;
          setMistakesShown(mistakes.current);
        }
      }
      if (isFull(g, s)) {
        const ok = g.solution ? wrongCells(g, s).length === 0 : conflictsOf(g, s).length === 0;
        if (ok) void finish();
        else showToast('Every cell is filled, but something is wrong.');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finished, paused, g, setHistory, digitsLive, wrongNow, finish, showToast],
  );

  const selected = useCallback(() => [...selection], [selection]);

  // A digit pressed with nothing selected selects every cell holding it, as a double tap does.
  // While that pick stands (the selection is still the one it made), another digit moves it
  // rather than overwriting, and matching candidates are highlighted (Marko, 2026-11-01).
  const [pick, setPickState] = useState<{ digit: string; cells: Set<number> } | null>(null);
  const pickRef = useRef<{ digit: string; cells: Set<number> } | null>(null);
  const pickDigit = useCallback(
    (digit: string) => {
      const cells = new Set(vals.flatMap((x, i) => (x === digit ? [i] : [])));
      pickRef.current = { digit, cells };
      setPickState(pickRef.current);
      setSelection(cells);
    },
    [vals],
  );
  const enter = useCallback(
    (digit: string, override?: Tool) => {
      const cells = selected();
      if (cells.length === 0 || selectionRef.current === pickRef.current?.cells) {
        pickDigit(digit);
        return;
      }
      const t = override ?? tool;
      if (t === 'colour') {
        const k = g.digits.indexOf(digit) + 1;
        if (k >= 1 && k <= 9) doMove({ kind: 'colour', cells, value: k });
        return;
      }
      if (t === 'pen') return;
      const kind =
        t === 'digit'
          ? 'digit'
          : t === 'centre'
            ? 'centre'
            : t === 'position'
              ? 'position'
              : t === 'pencil'
                ? 'pencil'
                : 'corner';
      doMove({
        kind,
        cells,
        value: digit,
        ...(kind === 'digit' && settings.clearNotesOnPlacement ? { auto: true } : {}),
      });
    },
    [selected, tool, g, doMove, settings.clearNotesOnPlacement, pickDigit],
  );

  // "Fill all candidates": every digit as centre marks (position marks while that tool is on;
  // pencil marks in Simple mode); pressed again on cells that are all full, it takes them off.
  const fill = useCallback(() => {
    const cells = selected();
    if (cells.length > 0)
      doMove({
        kind: 'fill',
        cells,
        markKey:
          inputModeRef.current === 'simple'
            ? 'corner'
            : tool === 'position'
              ? 'position'
              : 'centre',
      });
  }, [selected, doMove, tool]);

  const del = useCallback(() => {
    const cells = selected();
    if (cells.length > 0)
      doMove({ kind: 'delete', cells, value: tool === 'colour' ? 'colour' : null });
  }, [selected, doMove, tool]);

  const undo = useCallback(() => doMove({ kind: 'undo', cells: [] }), [doMove]);
  const redo = useCallback(() => doMove({ kind: 'redo', cells: [] }), [doMove]);

  // Pointer: tap selects, drag adds, Ctrl/Cmd/Shift adds, double-tap selects the same digit.
  const drag = useRef<{ active: boolean; pen: number | null; adding: boolean }>({
    active: false,
    pen: null,
    adding: false,
  });
  const lastTap = useRef<{ cell: number; at: number }>({ cell: -1, at: 0 });
  // Tapping the only selected cell deselects it, on release, unless the press became a drag.
  const tapToClear = useRef<number | null>(null);
  // The selection before the latest press, put back when that press turns into a pinch.
  const beforePress = useRef<Set<number> | null>(null);
  // The pointer of a press on a cell (React's handler runs before the document's listener below).
  const cellPress = useRef<number | null>(null);
  const onPointerDown = useCallback(
    (cell: number, e: ReactPointerEvent<SVGSVGElement>) => {
      cellPress.current = cell >= 0 ? e.pointerId : null;
      if (cell < 0 || paused) return;
      beforePress.current = selectionRef.current;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      if (tool === 'pen') {
        drag.current = { active: true, pen: cell, adding: false };
        return;
      }
      tapToClear.current = null;
      const now = Date.now();
      const v = vals[cell];
      if (!selecting && lastTap.current.cell === cell && now - lastTap.current.at < 350 && v) {
        pickDigit(v);
        lastTap.current = { cell: -1, at: 0 };
        return;
      }
      lastTap.current = { cell, at: now };
      const adding = selecting || e.ctrlKey || e.metaKey || e.shiftKey;
      drag.current = { active: true, pen: null, adding };
      const prev0 = selectionRef.current;
      if (!adding && prev0.size === 1 && prev0.has(cell)) tapToClear.current = cell;
      setSelection((prev) => {
        if (!adding) return new Set([cell]);
        const next = new Set(prev);
        if (next.has(cell)) next.delete(cell);
        else next.add(cell);
        return next;
      });
    },
    [tool, paused, vals, selecting, pickDigit],
  );
  const onPointerMove = useCallback(
    (cell: number) => {
      const d = drag.current;
      if (!d.active || cell < 0) return;
      if (d.pen !== null) {
        if (cell !== d.pen) {
          const [ra, ca] = g.cells[d.pen]!;
          const [rb, cb] = g.cells[cell]!;
          if (Math.abs(ra - rb) <= 1 && Math.abs(ca - cb) <= 1)
            doMove({ kind: 'pen', cells: [d.pen, cell] });
          d.pen = cell;
        }
        return;
      }
      if (tapToClear.current !== null && cell !== tapToClear.current) tapToClear.current = null;
      setSelection((prev) => (prev.has(cell) ? prev : new Set([...prev, cell])));
    },
    [g, doMove],
  );
  const onPointerUp = useCallback(() => {
    drag.current = { active: false, pen: null, adding: false };
    beforePress.current = null;
    if (tapToClear.current !== null) {
      tapToClear.current = null;
      setSelection(new Set());
    }
  }, []);
  const onGestureStart = useCallback(() => {
    drag.current = { active: false, pen: null, adding: false };
    tapToClear.current = null;
    lastTap.current = { cell: -1, at: 0 };
    if (beforePress.current) setSelection(beforePress.current);
    beforePress.current = null;
  }, []);

  // Tapping anywhere outside the grid's cells clears the selection, as in SudokuPad (Marko,
  // 2026-11-01): not on buttons, links, fields, dialogs or anywhere in the pad block (gaps too),
  // and only a real tap: one finger or the main mouse button, let go near where it went down. A
  // drag that starts on the grid and a two-finger pinch don't count; the grid's frame (outside
  // clues) counts as outside; the select tool doesn't change it.
  useEffect(() => {
    let press: { id: number; x: number; y: number } | null = null;
    const down = new Set<number>();
    const keeps = (t: EventTarget | null) =>
      t instanceof Element &&
      t.closest(
        'button, a, input, textarea, select, label, [role="dialog"], [role="switch"], [data-keeps-selection]',
      ) !== null;
    const onDown = (e: PointerEvent) => {
      down.add(e.pointerId);
      const onCell = cellPress.current === e.pointerId;
      cellPress.current = null;
      press =
        down.size === 1 && e.isPrimary && e.button <= 0 && !onCell && !keeps(e.target)
          ? { id: e.pointerId, x: e.clientX, y: e.clientY }
          : null;
    };
    const onUp = (e: PointerEvent) => {
      down.delete(e.pointerId);
      const p = press;
      press = null;
      if (!p || p.id !== e.pointerId) return;
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) return;
      setSelection((s) => (s.size === 0 ? s : new Set()));
    };
    const onCancel = (e: PointerEvent) => {
      down.delete(e.pointerId);
      press = null;
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
  }, []);

  // Keyboard, as in SudokuPad: digits; Shift corner, Ctrl centre; Z X C V tools, and B for
  // position marks; Space cycles; arrows move (Shift/Ctrl extends); Backspace/Delete; Ctrl+Z / Y.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (menuOpen || finishInfo || settingsOpen || addingToCollection) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const key = e.key;
      if (ctrl && (key === 'z' || key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (ctrl && (key === 'y' || key === 'Y')) {
        e.preventDefault();
        redo();
        return;
      }
      if (ctrl && (key === 'a' || key === 'A')) {
        e.preventDefault();
        setSelection(new Set(g.cells.map((_, i) => i)));
        return;
      }
      const digitFromCode = /^(?:Digit|Numpad)(\d)$/.exec(e.code)?.[1];
      let digit = g.digits.find((d) => d.toLowerCase() === key.toLowerCase()) ?? null;
      if (!digit && digitFromCode !== undefined)
        digit = g.digits.find((d) => d === digitFromCode) ?? null;
      // Windows turns Shift + a numpad digit (Num Lock on) into the key printed under it (End,
      // Clear, ArrowDown…) and reports Shift as up; e.code still names the digit. So a numpad
      // digit that arrives as another key while Num Lock is on was pressed with Shift (Marko,
      // 2026-11-01). Ctrl comes through as it is.
      const numpad = /^Numpad(\d)$/.exec(e.code)?.[1];
      const shift =
        e.shiftKey ||
        (numpad !== undefined && key !== numpad && e.getModifierState?.('NumLock') === true);
      if (
        digit &&
        !(['z', 'x', 'c', 'v'].includes(key.toLowerCase()) && !g.digits.includes(key.toUpperCase()))
      ) {
        e.preventDefault();
        if (shift && ctrl && !showColour) return;
        const override: Tool | undefined =
          shift && ctrl
            ? 'colour'
            : shift
              ? inputModeRef.current === 'simple'
                ? 'pencil'
                : 'corner'
              : ctrl
                ? 'centre'
                : undefined;
        enter(digit, override);
        return;
      }
      if (key === 'Backspace' || key === 'Delete') {
        e.preventDefault();
        del();
        return;
      }
      if (key === 'Escape') {
        setSelection(new Set());
        return;
      }
      if (inputModeRef.current === 'sudokupad' && !ctrl) {
        const tools: Record<string, Tool> = {
          z: 'digit',
          x: 'corner',
          c: 'centre',
          b: 'position',
          ...(showColour ? { v: 'colour' as const } : {}),
        };
        const t = tools[key.toLowerCase()];
        if (t) {
          setTool(t);
          return;
        }
        if (key === ' ') {
          e.preventDefault();
          // Marks hidden in Settings are left out; their keys (X, C, B) still work.
          const cycle: Tool[] = [
            'digit',
            ...(markButtons.corner ? ['corner' as const] : []),
            ...(markButtons.centre ? ['centre' as const] : []),
            ...(markButtons.position ? ['position' as const] : []),
            ...(showColour ? ['colour' as const] : []),
          ];
          setTool((cur) => cycle[(cycle.indexOf(cur) + 1) % cycle.length]!);
          return;
        }
      }
      const dirs: Record<string, [number, number]> = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      };
      const dir = dirs[key];
      if (dir) {
        e.preventDefault();
        const prev = selectionRef.current;
        const last = [...prev].pop() ?? 0;
        let [r, c] = g.cells[last] ?? [0, 0];
        let next = -1;
        for (let step = 0; step < Math.max(g.rows, g.cols) && next < 0; step++) {
          r = (r + dir[0] + g.rows) % g.rows;
          c = (c + dir[1] + g.cols) % g.cols;
          next = cellAt(g, r, c);
        }
        if (next < 0) return;
        let s = new Set([next]);
        if (e.shiftKey || ctrl) {
          s = new Set(prev);
          s.delete(next);
          s.add(next);
        }
        selectionRef.current = s;
        setSelection(s);
        // Zoomed in: keep the moved-to cell on screen.
        zoom.current?.reveal(g.cells[next]!);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    g,
    enter,
    del,
    undo,
    redo,
    menuOpen,
    finishInfo,
    settingsOpen,
    addingToCollection,
    showColour,
    markButtons,
  ]);

  const switchMode = (mode: InputMode) => {
    setInputMode(mode);
    setTool('digit');
    syncRef.current?.touch();
  };

  const check = () => {
    const wrong =
      settings.mistakeChecking === 'solution' && g.solution
        ? new Set([...conflictCells, ...wrongCells(g, state)])
        : conflictCells;
    setChecked(wrong);
    const shows = givenShows(g, state);
    const count = [...wrong].filter((i) => !shows(i)).length;
    showToast(
      count === 0
        ? 'No problems found.'
        : `${count} ${count === 1 ? 'cell looks' : 'cells look'} wrong.`,
    );
  };

  const restart = async () => {
    if (!window.confirm('Restart this puzzle? Your current attempt is kept in your history.'))
      return;
    const fresh = await api<SolveDto>(`/api/solves/${solve.id}/restart`, { method: 'POST' }).catch(
      () => null,
    );
    if (!fresh) {
      showToast("Couldn't restart: no connection.");
      return;
    }
    setSolve(fresh);
    setHistory(replay(g, []));
    allMoves.current = [];
    recentCells.current = [];
    setNudge(null);
    mistakes.current = 0;
    nudges.current = 0;
    setMistakesShown(0);
    timer.reset(0);
    setFinished(false);
    setPaused(false);
    setSelection(new Set());
    makeSync(fresh);
  };

  const exportLink = async () => {
    try {
      const link = coreAvailable() ? await core.exportLink(doc) : null;
      if (!link) throw new Error('no link');
      await navigator.clipboard?.writeText(link);
      showToast('SudokuPad link copied.');
    } catch {
      showToast("Couldn't make a SudokuPad link here.");
    }
  };

  const countNudge = () => {
    nudges.current += 1;
    syncRef.current?.touch();
  };

  // Level 4: apply the step as one move (placements, a cleared mistake, or the marks left).
  const applyNudge = (answer: NudgeAnswer) => {
    const at = (rc: [number, number]) => cellAt(g, rc[0], rc[1]);
    if (answer.kind === 'mistake') {
      const clear = answer.clear.map(at).filter((i) => i >= 0);
      doMove({ kind: 'nudge', cells: clear, clear });
      return;
    }
    const place = answer.place
      .map(([rc, d]) => [at(rc), d] as [number, string])
      .filter(([i]) => i >= 0);
    const marks = answer.remaining
      .map(([rc, list]) => [at(rc), list] as [number, string[]])
      .filter(([i]) => i >= 0);
    const cells = [...new Set([...place.map((p) => p[0]), ...marks.map((m) => m[0])])];
    doMove({
      kind: 'nudge',
      cells,
      place,
      marks,
      markKey:
        inputModeRef.current === 'simple' ? 'corner' : tool === 'position' ? 'position' : 'centre',
    });
  };

  const pressNudge = async () => {
    if (finished || paused) return;
    if (nudge) {
      countNudge();
      if (nudge.level < nudge.answer.levels.length) {
        setNudge({ ...nudge, level: nudge.level + 1 });
      } else {
        setNudge(null);
        applyNudge(nudge.answer);
      }
      return;
    }
    if (!coreAvailable()) {
      showToast('Nudges need the solver, which this browser can’t run.');
      return;
    }
    if (nudgeBusy.current) return;
    nudgeBusy.current = true;
    const asked = historyRef.current;
    const s = current(asked);
    try {
      const answer = await core.nudge({
        doc,
        // Under fog the core works out which givens show, from the digits the player typed.
        values: doc.fog ? typed(s) : values(g, s),
        // Position marks are candidates too: a cell with only those sends them as its centre marks.
        centre: s.cells.map((c) => (c.centre.length ? c.centre : c.position)),
        corner: s.cells.map((c) => c.corner),
        mode: inputModeRef.current,
        recent: recentCells.current,
      });
      if (historyRef.current !== asked) return; // a move came first: ask again
      if (answer.kind === 'solved') showToast('Every digit is in, and right.');
      else if (answer.kind === 'unavailable')
        showToast(answer.message ?? 'There is no nudge for this puzzle.');
      else {
        countNudge();
        setNudge({ answer, level: 1 });
      }
    } catch {
      showToast('Couldn’t find a nudge here.');
    } finally {
      nudgeBusy.current = false;
    }
  };

  const rate = (next: Thumb | null) => {
    const before = rating;
    setRating(next);
    api<RatingResponse>(`/api/puzzles/${puzzle.id}/rating`, {
      method: 'PUT',
      json: { rating: next },
    }).catch(() => {
      setRating(before);
      showToast('Couldn’t save that: no connection.');
    });
  };

  const startReplay = () => {
    setFinishInfo(null);
    setNudge(null);
    setSelection(new Set());
    setReplaying({ states: replayStates(g, allMoves.current), at: 0, playing: true });
  };

  const menuActions: MenuAction[] = [
    {
      key: 'nudge',
      label: 'Nudge',
      icon: menuIcons.nudge,
      onSelect: () => void pressNudge(),
      disabled: finished || puzzle.status === 'needs-plugin',
      note:
        puzzle.status === 'needs-plugin'
          ? 'Needs a plugin first'
          : nudge
            ? 'One level deeper'
            : undefined,
    },
    ...(finished
      ? [{ key: 'replay', label: 'Replay', icon: menuIcons.restart, onSelect: startReplay }]
      : []),
    ...(inputMode === 'sudokupad'
      ? [{ key: 'check', label: 'Check', icon: menuIcons.check, onSelect: check }]
      : []),
    {
      key: 'restart',
      label: 'Restart',
      icon: menuIcons.restart,
      onSelect: () => void restart(),
      divider: true,
    },
    { key: 'rules', label: 'Rules', icon: menuIcons.rules, onSelect: () => setRulesOpen(true) },
    {
      key: 'collection',
      label: 'Add to collection',
      icon: menuIcons.collection,
      onSelect: () => setAddingToCollection(true),
    },
    {
      key: 'mode',
      label: inputMode === 'sudokupad' ? 'Switch to Simple mode' : 'Switch to SudokuPad mode',
      icon: menuIcons.settings,
      onSelect: () => switchMode(inputMode === 'sudokupad' ? 'simple' : 'sudokupad'),
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: menuIcons.settings,
      onSelect: () => setSettingsOpen(true),
    },
    {
      key: 'export',
      label: 'Export as SudokuPad link',
      icon: menuIcons.export,
      onSelect: () => void exportLink(),
    },
  ];

  // Where the nudge says to look.
  const hint = useMemo(() => {
    if (!nudge) return undefined;
    const cells = nudge.answer.levels[nudge.level - 1]?.cells ?? [];
    return new Set(cells.map(([r, c]) => cellAt(g, r, c)).filter((i) => i >= 0));
  }, [nudge, g]);

  // Replay plays the move log back, a few moves a second.
  useEffect(() => {
    if (!replaying?.playing) return;
    const id = setInterval(() => {
      setReplaying((r) => {
        if (!r) return r;
        const at = Math.min(r.at + 1, r.states.length - 1);
        return { ...r, at, playing: at < r.states.length - 1 };
      });
    }, 250);
    return () => clearInterval(id);
  }, [replaying?.playing]);
  const shownState = replaying ? replaying.states[replaying.at]! : state;

  // Simple mode highlights.
  const single = selection.size === 1 ? [...selection][0]! : -1;
  const houseHighlight = useMemo(() => {
    if (inputMode !== 'simple' || !settings.highlightHouses || single < 0) return undefined;
    return new Set(g.houses.filter((h) => h.includes(single)).flat());
  }, [inputMode, settings.highlightHouses, single, g]);
  // "One tap highlights the digit" (a person's setting, both modes): a tap on a cell with a digit
  // tints that digit everywhere, with its candidates, as a double tap does, while only the tapped
  // cell stays selected (so typing or erasing still changes just that cell).
  const tappedDigit = settings.tapHighlightsDigit && single >= 0 ? vals[single] : null;
  const matchingDigits = useMemo(() => {
    const on = tappedDigit !== null || (inputMode === 'simple' && settings.highlightMatchingDigits);
    if (!on || single < 0) return undefined;
    const v = vals[single];
    return v ? new Set(vals.flatMap((x, i) => (x === v ? [i] : []))) : undefined;
  }, [inputMode, settings.highlightMatchingDigits, tappedDigit, single, vals]);
  // Both modes, corner and centre marks alike, same tint as matching digits; only while the
  // selection is a digit pick (a double tap or the pad), not a plain tap (Marko, 2026-11-01).
  const pickedDigit = pick && pick.cells === selection ? pick.digit : tappedDigit;
  const matching = useMemo(() => {
    const candidates =
      settings.highlightMatchingCandidates && pickedDigit
        ? matchingCandidates(state, vals, pickedDigit)
        : new Set<number>();
    if (candidates.size === 0) return matchingDigits;
    return new Set([...(matchingDigits ?? []), ...candidates]);
  }, [settings.highlightMatchingCandidates, state, vals, pickedDigit, matchingDigits]);

  const subtitle = [puzzle.setter, puzzle.origin, SOURCE_LABELS[puzzle.source] ?? puzzle.source]
    .filter(Boolean)
    .join(' · ');
  const summary = rulesSummary(
    doc.meta.rules,
    puzzle.status === 'needs-plugin' ? [] : doc.constraints.map((c) => c.type),
  );

  return (
    <div className="relative mx-auto flex min-h-dvh w-full max-w-[1280px] flex-col lg:h-dvh">
      <SolveHeader
        title={puzzle.title}
        subtitle={subtitle}
        elapsedMs={timer.shown}
        paused={paused}
        hideTimer={settings.hideTimer}
        finished={finished}
        menuOpen={menuOpen}
        onBack={() => void navigate(-1)}
        onTimer={() => setPaused((p) => !p)}
        onMenu={() => setMenuOpen((o) => !o)}
      />
      {menuOpen && <Menu actions={menuActions} onClose={() => setMenuOpen(false)} />}
      {!online && (
        <div
          className="mx-3 mb-2 rounded-btn bg-warn-bg px-3 py-1.5 text-[13px] font-semibold text-warn-ink"
          role="status"
        >
          Offline: your moves are kept and will be saved when you're back.
        </div>
      )}
      {puzzle.status === 'needs-plugin' && (
        <div className="mx-3 mb-2 rounded-btn bg-unrated-bg px-3 py-1.5 text-[13px] text-unrated-ink">
          Needs a plugin · playable without checking
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row lg:gap-6 lg:px-6 lg:pb-6">
        <div className="flex flex-col lg:min-h-0 lg:flex-1 lg:items-center lg:justify-center">
          <div className="lg:hidden">
            <RulesStrip
              doc={doc}
              summary={summary}
              open={rulesOpen}
              onToggle={() => setRulesOpen((o) => !o)}
              side={
                inputMode === 'simple' && digitsLive ? (
                  <span className="flex h-10 items-center rounded-btn bg-mistake-bg px-3 text-[13px] font-bold whitespace-nowrap text-[#A3212D]">
                    Mistakes {mistakesShown}
                  </span>
                ) : undefined
              }
            />
          </div>
          <div
            className="relative mx-auto box-border w-full max-w-[max(260px,calc((100dvh-360px)*var(--grid-ratio)))] short:max-w-[max(240px,calc((100dvh-320px)*var(--grid-ratio)))] px-3 pt-2 lg:max-w-[calc((100dvh-110px)*var(--grid-ratio))] lg:px-0 lg:pt-0"
            style={{ '--grid-ratio': frame.w / frame.h } as React.CSSProperties}
          >
            <ZoomView
              ref={zoom}
              frame={frame}
              enabled={zoomable && !paused}
              maxScale={Math.max(2, Math.max(g.rows, g.cols) / 5)}
              onGestureStart={onGestureStart}
              overview={
                doc.grid.subGrids && doc.grid.subGrids.length > 1
                  ? (view, jump) => (
                      <Overview
                        doc={doc}
                        geometry={g}
                        state={state}
                        frame={frame}
                        view={view}
                        onJump={jump}
                      />
                    )
                  : undefined
              }
            >
              {(view) => (
                <Grid
                  doc={doc}
                  geometry={g}
                  state={shownState}
                  selection={selection}
                  houseHighlight={houseHighlight}
                  matching={matching}
                  hint={hint}
                  wrong={replaying ? undefined : shownWrong}
                  wrongMarks={replaying ? undefined : shownWrongMarks}
                  markStyle={inputMode}
                  paused={paused}
                  view={view}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  label={`${puzzle.title}: ${g.rows}×${g.cols} grid`}
                />
              )}
            </ZoomView>
            {paused && (
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setPaused(false)}
                  className="h-12 rounded-[12px] border-0 bg-accent px-6 text-[16px] font-bold text-white"
                >
                  Paused · Resume
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-end gap-3 px-3 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] lg:w-[420px] lg:flex-none lg:justify-start lg:px-0 lg:pt-0">
          <div className="hidden lg:block">
            <RulesStrip
              doc={doc}
              summary={summary}
              open={true}
              onToggle={() => {}}
              overlay={false}
            />
            {inputMode === 'simple' && digitsLive && (
              <div className="mx-3 mt-2 inline-flex h-10 items-center rounded-btn bg-mistake-bg px-3 text-[13px] font-bold text-[#A3212D]">
                Mistakes {mistakesShown}
              </div>
            )}
          </div>
          {nudge && (
            <NudgeCard
              answer={nudge.answer}
              level={nudge.level}
              onMore={() => void pressNudge()}
              onClose={() => setNudge(null)}
            />
          )}
          {replaying ? (
            <ReplayBar
              at={replaying.at}
              total={replaying.states.length - 1}
              playing={replaying.playing}
              onPlay={() =>
                setReplaying((r) =>
                  r
                    ? r.playing
                      ? { ...r, playing: false }
                      : { ...r, at: r.at >= r.states.length - 1 ? 0 : r.at, playing: true }
                    : r,
                )
              }
              onStep={(delta) =>
                setReplaying((r) =>
                  r
                    ? {
                        ...r,
                        playing: false,
                        at: Math.max(0, Math.min(r.states.length - 1, r.at + delta)),
                      }
                    : r,
                )
              }
              onClose={() => setReplaying(null)}
            />
          ) : (
            <Pad
              inputMode={inputMode}
              tool={tool}
              digits={g.digits}
              onTool={setTool}
              onDigit={(d) => enter(d)}
              onColour={(k) => {
                const cells = selected();
                if (cells.length) doMove({ kind: 'colour', cells, value: k });
              }}
              onDelete={del}
              onUndo={undo}
              onRedo={redo}
              canUndo={canUndo(history) && !finished}
              canRedo={canRedo(history) && !finished}
              showColour={showColour}
              showPen={showPen}
              showFill={settings.showFillButton}
              onFill={fill}
              selecting={selecting}
              onSelecting={settings.showSelectTool ? setSelectOn : undefined}
              onSettings={settings.showSettingsButton ? () => setSettingsOpen(true) : undefined}
              onRestart={() => void restart()}
              marks={markButtons}
              layout={settings.padLayout}
              toolsAt={settings.padToolsAt}
              looks={settings.padLooks}
            />
          )}
        </div>
      </div>
      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-[12px] bg-ink px-4 py-2.5 text-[14px] font-semibold text-white shadow-lg"
        >
          {toast}
        </div>
      )}
      {settingsOpen && <SettingsSheet settings={settings} onClose={() => setSettingsOpen(false)} />}
      {finishInfo && (
        <FinishDialog
          timeMs={finishInfo.timeMs}
          nudges={finishInfo.nudges}
          score={puzzle.score}
          otherName={otherName}
          other={finishInfo.other}
          message={doc.meta.finishMessage ?? null}
          rating={rating}
          onRate={rate}
          onAddToCollection={() => setAddingToCollection(true)}
          onReplay={startReplay}
          onLibrary={() => void navigate('/library')}
          onClose={() => setFinishInfo(null)}
        />
      )}
      {addingToCollection && (
        <AddToCollectionDialog puzzleId={puzzle.id} onClose={() => setAddingToCollection(false)} />
      )}
    </div>
  );
}
